import {
  createI18nProvider as createBaseI18nProvider,
  useTranslator,
} from '@bluebeam/react-i18n';
import LoadingAnimation from 'components/LoadingAnimation';
import { languageOptions } from 'constants/languages';
import { RegionConfig } from 'constants/regionConfig';
import { environmentVars } from 'constants/services';
import daDK from 'locales/da-DK/local.json';
import deDE from 'locales/de-DE/local.json';
import enAU from 'locales/en-AU/local.json';
import enGB from 'locales/en-GB/local.json';
import enUS from 'locales/en-US/local.json';
import svSE from 'locales/sv-SE/local.json';
import * as React from 'react';

const SuspendedTranslation: React.FC = ({ children }) => {
  const { translate } = useTranslator(['local']);
  return (
    <React.Suspense
      fallback={
        <main
          css={{
            alignItems: 'center',
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            height: '100vh',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          <LoadingAnimation pageName={translate('local:loading')} />
        </main>
      }
    >
      {children}
    </React.Suspense>
  );
};

const createI18nProvider = (): React.FC => {
  const customLanguageDetector = {
    name: 'customLanguageDetector',
    lookup: () => {
      const browserLanguage = navigator.languages.find((lng) =>
        languageOptions.some((language) => language.value === lng)
      );

      if (browserLanguage) {
        return browserLanguage;
      }

      const regionConfigs = RegionConfig.map((config) => ({
        id: config.countryCode,
        href: config.dashboardUrl,
        label: config.labelTranslationKey,
        languageCode: config.languageKey,
      }));

      const currentRegion = regionConfigs.find(
        (r) => r.href === window.location.origin
      );

      return currentRegion?.languageCode;
    },
  };

  const I18nProvider = createBaseI18nProvider(
    environmentVars.I18N_BASE_RESOURCE_URL,
    {
      'de-DE': deDE,
      'en-AU': enAU,
      'en-GB': enGB,
      'en-US': enUS,
      'sv-SE': svSE,
    },
    customLanguageDetector
  );

  return ({ children }) => {
    return (
      <I18nProvider>
        <SuspendedTranslation>{children}</SuspendedTranslation>
      </I18nProvider>
    );
  };
};

export { createI18nProvider };
